import { createPopper, Instance, Placement } from '@popperjs/core'
import clsx from 'clsx'
import React, { useCallback, useEffect, useRef } from 'react'

import { Icon } from '../Icon'
import { Portal } from '../Portal'
import { useCombinedRef } from '../useCombinedRef'
import { KeyCodes } from '../utils/keyboard'
import { CARET_PLACEMENTS, KINDS, SELECTOR_CANDIDATES } from './constants'
import styles from './style.module.scss'
import { TooltipProps } from './types'
import { isCallable } from '@afterpay/utils'
import { useThemeClass } from '../Theme'
import { sameWidthModifier } from './utils'

type CSSClasses = keyof typeof styles

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      id,
      ariaIsOpen,
      kind = KINDS.DEFAULT,
      hasCaret = true,
      caretPlacement = CARET_PLACEMENTS.TOP_START,
      onClose,
      usePortal = true,
      skidding = 0,
      flippable = true,
      testNameSpace = '',
      content,
      bubbleSize,
      triggerRef,
      inline = false,
      resize = true,
      verticalOffset = 10,
      useMobileView = false,
      closeOnOutsideClick = undefined,
      matchTriggerWidth = false,
    },
    ref
  ) => {
    const tooltipRef = useRef(null)
    const tooltipMobileRef = useRef<HTMLDivElement | null>(null)
    const closeBtnRef = useRef<HTMLButtonElement | null>(null)
    const combinedRef = useCombinedRef(tooltipRef, ref)
    const themeClass = useThemeClass()
    const ariaHidden = typeof ariaIsOpen === 'undefined' ? undefined : !ariaIsOpen
    const tooltipClassNames = clsx(
      styles.tooltip,
      useMobileView && styles.tooltipMobile,
      styles[`tooltip${kind}` as CSSClasses],
      inline && styles.inline,
      isCallable(onClose) && styles.withCloseButton,
      styles[themeClass]
    )

    const closeButtonClassNames = clsx(styles.closeButtonContainer, styles[themeClass])
    const caretClassNames = clsx(styles.caret)

    const createPopperInstance = useCallback((): Instance | undefined => {
      if (typeof triggerRef === 'undefined') {
        return
      }

      const instance = createPopper(triggerRef.current as HTMLElement, combinedRef.current as HTMLElement, {
        placement: caretPlacement as Placement,
        modifiers: [
          sameWidthModifier(matchTriggerWidth),
          {
            name: 'offset',
            options: {
              offset: [-skidding, verticalOffset],
            },
          },
          {
            name: 'arrow',
            options: {
              padding: skidding,
            },
          },
          {
            name: 'flip',
            enabled: flippable,
          },
          {
            name: 'eventListeners',
            options: {
              resize,
            },
          },
        ],
      })

      return instance
    }, [caretPlacement, combinedRef, flippable, skidding, triggerRef, matchTriggerWidth])

    const handleKeyDown = (evt: KeyboardEvent): void => {
      if ((evt.key as KeyCodes) === KeyCodes.Escape) {
        closeBtnRef.current?.click()
      }
    }

    const registerKeyDownListener = useCallback((): void => {
      window.addEventListener('keydown', handleKeyDown)
    }, [])

    const unregisterKeyDownListener = useCallback((): void => {
      window.removeEventListener('keydown', handleKeyDown)
    }, [])

    useEffect(() => {
      const instance = createPopperInstance()

      return () => {
        if (typeof instance !== 'undefined') {
          instance.destroy()
        }
      }
    }, [createPopperInstance])

    useEffect(() => {
      const containerElement = combinedRef.current as HTMLDivElement
      if (typeof containerElement === 'undefined') {
        return
      }

      const tabbableElements = containerElement.querySelectorAll(SELECTOR_CANDIDATES.join(', '))
      if (tabbableElements.length > 0) {
        ;(tabbableElements[0] as HTMLElement).focus({ preventScroll: true })
      }
    }, [combinedRef])

    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event: MouseEvent): void {
        const desktopTooltipTrigger = combinedRef?.current && !combinedRef?.current.contains(event.target)
        const mobileTooltipTrigger = tooltipMobileRef?.current != null && !tooltipMobileRef?.current.contains(event.target as Node)
        if (desktopTooltipTrigger || mobileTooltipTrigger) {
          if (isCallable(closeOnOutsideClick)) {
            closeOnOutsideClick(true)
          }
          if (closeBtnRef.current) {
            closeBtnRef.current.click()
          }
        }
      }

      if (isCallable(closeOnOutsideClick) || closeOnOutsideClick === true) {
        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
          // Unbind the event listener on clean up
          document.removeEventListener('mousedown', handleClickOutside)
        }
      }
    }, [tooltipMobileRef, combinedRef, closeOnOutsideClick])

    useEffect(() => {
      // A11y notes: The close button is always the last tabbable element. If this button loses the focus,
      // we can assume that Tooltip is out of focus. So remove Escape key listener immediately.
      registerKeyDownListener()

      return () => {
        unregisterKeyDownListener()
      }
    }, [registerKeyDownListener, unregisterKeyDownListener])

    const renderContent = (): React.ReactElement => (
      <div
        id={`${id}`}
        role='tooltip'
        ref={useMobileView ? tooltipMobileRef : combinedRef}
        className={tooltipClassNames}
        style={{ width: bubbleSize?.width, height: bubbleSize?.height }}
        aria-hidden={ariaHidden}
        {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-content` })}
      >
        {hasCaret && <div data-popper-arrow className={caretClassNames}></div>}
        <div>{content}</div>
        {isCallable(onClose) && (
          <button
            ref={closeBtnRef}
            type='button'
            className={closeButtonClassNames}
            onClick={onClose}
            onBlur={unregisterKeyDownListener}
            aria-label='Close'
            {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-close-btn` })}
          >
            <Icon.Close size='Small' title='Dismiss' />
          </button>
        )}
      </div>
    )

    return usePortal ? <Portal>{renderContent()}</Portal> : renderContent()
  }
)
